// Family definitions and constants to avoid hardcoding magic numbers throughout the codebase

// Family ID constants for easy reference
export const FamilyIds = {
  // Transport
  TRAIN: 8,
  TRAIN_ALTERNATIVE: 83,
  FLIGHT: 81,
  TRANSFER: 78,
  
  // Luggage
  LUGGAGE: 82,
  
  // Equipment & Services
  SKIPASS: 3,
  HELMET: 30,
  EQUIPMENT: 14, // Ski Equipment TW
  EQUIPMENT_IND: 53, // Ski Equipment Ind
  EQUIPMENT_IS: 54, // Ski Equipment IS
  EQUIPMENT_SK: 55, // Ski Equipment SK
  EQUIPMENT_SP: 56, // Ski Equipment SP
  /** Promo / price adjustment (booking-funnel `PRICE_ADJUSTMENT_PROMO`) — not equipment. */
  PRICE_ADJUSTMENT_PROMO: 57,
  EQUIPMENT_DS: 63, // Ski Equipment DS
  
  // Accommodation & Services
  HOTEL_SERVICES: 4,
  ACCOMMODATION: 73, // Additional Accommodation
  
  // Activities
  CATERING: 11,
  SKI_SCHOOL: 12,
  SPA_WELLNESS: 13,
  GOURMET: 40,
  SPORT: 41,
  AMUSEMENT_PARK: 42,
  THEME_PARK: 66,
  CYCLING: 72,
  OUTDOOR_HIKING: 74,
  ACTIVITIES: 77,
  
  // Other Services
  GROCERY_DELIVERY: 5,
  YOUR_SERVICES: 7,
  SPECIAL_OFFER: 38,
  PARTNER_EVENT: 68,
  PARKING: 69,
  TOURIST_TAX: 70,
  ON_SITE_SALES: 71,
  LUGGAGE_STORAGE: 67,
  FAMILY_SKIPASS: 75,
  MULTI_ACTIVITY: 65,
  
  // Legacy/Regional
  SKI_SCHOOL_NL: 64,
} as const

// Type for family IDs
export type FamilyId = typeof FamilyIds[keyof typeof FamilyIds]

// Family definitions with names
export const FamilyDefinitions = [
  {
    familyId: FamilyIds.SKIPASS,
    familyName: 'Skipass - Ski Pass',
  },
  {
    familyId: FamilyIds.HOTEL_SERVICES,
    familyName: 'Hotel Services',
  },
  {
    familyId: FamilyIds.GROCERY_DELIVERY,
    familyName: 'Grocery Basket Delivery',
  },
  {
    familyId: FamilyIds.YOUR_SERVICES,
    familyName: 'Your Services',
  },
  {
    familyId: FamilyIds.TRAIN,
    familyName: 'Transport',
  },
  {
    familyId: FamilyIds.CATERING,
    familyName: 'Catering',
  },
  {
    familyId: FamilyIds.SKI_SCHOOL,
    familyName: 'Ski School - Group Lessons',
  },
  {
    familyId: FamilyIds.SPA_WELLNESS,
    familyName: 'Spa & Wellness Activities',
  },
  {
    familyId: FamilyIds.EQUIPMENT,
    familyName: 'Ski Equipment TW',
  },
  {
    familyId: FamilyIds.HELMET,
    familyName: 'Helmet',
  },
  {
    familyId: FamilyIds.SPECIAL_OFFER,
    familyName: 'Special Offer',
  },
  {
    familyId: FamilyIds.GOURMET,
    familyName: 'Gourmet Activities',
  },
  {
    familyId: FamilyIds.SPORT,
    familyName: 'Sport',
  },
  {
    familyId: FamilyIds.AMUSEMENT_PARK,
    familyName: 'Amusement Park',
  },
  {
    familyId: FamilyIds.EQUIPMENT_IND,
    familyName: 'Ski Equipment Ind',
  },
  {
    familyId: FamilyIds.EQUIPMENT_IS,
    familyName: 'Ski Equipment IS',
  },
  {
    familyId: FamilyIds.EQUIPMENT_SK,
    familyName: 'Ski Equipment SK',
  },
  {
    familyId: FamilyIds.EQUIPMENT_SP,
    familyName: 'Ski Equipment SP',
  },
  {
    familyId: FamilyIds.PRICE_ADJUSTMENT_PROMO,
    familyName: 'Promo code adjustment',
  },
  {
    familyId: FamilyIds.EQUIPMENT_DS,
    familyName: 'Ski Equipment DS',
  },
  {
    familyId: FamilyIds.SKI_SCHOOL_NL,
    familyName: 'Ski School - Group Lessons NL',
  },
  {
    familyId: FamilyIds.MULTI_ACTIVITY,
    familyName: 'Multi-Activity Pass',
  },
  {
    familyId: FamilyIds.THEME_PARK,
    familyName: 'Theme Park',
  },
  {
    familyId: FamilyIds.LUGGAGE_STORAGE,
    familyName: 'Luggage Storage',
  },
  {
    familyId: FamilyIds.PARTNER_EVENT,
    familyName: 'Partner Event',
  },
  {
    familyId: FamilyIds.PARKING,
    familyName: 'Parking',
  },
  {
    familyId: FamilyIds.TOURIST_TAX,
    familyName: 'Tourist Tax',
  },
  {
    familyId: FamilyIds.ON_SITE_SALES,
    familyName: 'On-Site Sales',
  },
  {
    familyId: FamilyIds.CYCLING,
    familyName: 'Cycling Activities',
  },
  {
    familyId: FamilyIds.ACCOMMODATION,
    familyName: 'Additional Accommodation',
  },
  {
    familyId: FamilyIds.OUTDOOR_HIKING,
    familyName: 'Outdoor & Hiking Activities',
  },
  {
    familyId: FamilyIds.FAMILY_SKIPASS,
    familyName: 'Family Skipass',
  },
  {
    familyId: FamilyIds.ACTIVITIES,
    familyName: 'Activities',
  },
  {
    familyId: FamilyIds.TRANSFER,
    familyName: 'Transfers',
  },
]

// Helper function to check if a family ID is a transport type
export const isTransportFamily = (familyId: number): boolean => {
  return [
    FamilyIds.TRAIN,
    FamilyIds.TRAIN_ALTERNATIVE,
    FamilyIds.FLIGHT,
    FamilyIds.TRANSFER
  ].includes(familyId as any)
}

// Helper function to check if a family ID is luggage
export const isLuggageFamily = (familyId: number): boolean => {
  return familyId === FamilyIds.LUGGAGE
}

// Helper function to check if a family ID is equipment
export const isEquipmentFamily = (familyId: number): boolean => {
  return [
    FamilyIds.EQUIPMENT,
    FamilyIds.EQUIPMENT_IND,
    FamilyIds.EQUIPMENT_IS,
    FamilyIds.EQUIPMENT_SK,
    FamilyIds.EQUIPMENT_SP,
    FamilyIds.EQUIPMENT_DS,
    FamilyIds.HELMET
  ].includes(familyId as any)
}

// Helper function to get family name by ID
export const getFamilyName = (familyId: number): string => {
  const family = FamilyDefinitions.find(f => f.familyId === familyId)
  return family?.familyName || 'Unknown Family'
}

// Helper function to get family by ID
export const getFamily = (familyId: number) => {
  return FamilyDefinitions.find(f => f.familyId === familyId)
}
